import dayjs from 'dayjs'
import { formatDateTime } from '@/lib/dates'

export interface MeetingActionItem {
  owner: string | null
  text: string
  seconds: number | null
  url: string | null
}

export type RequestFlag = { kind: 'fixed' } | { kind: 'declined' } | { kind: 'in_linear' } | { kind: 'raised_before'; count: number }

export type RequestLinkKind = 'ticket' | 'linear'

/** Where a request was raised: a call or a logged email. */
export interface RequestSource {
  kind: 'call' | 'email'
  _id: string
  title: string
  date: string
}

export interface RequestHistoryEntry {
  date: string
  outcome: 'linked' | 'created' | 'dismissed' | 'not_logged'
  ref?: string
  byName?: string
}

export interface CallRequest {
  _id: string
  meetingId: string | null
  emailId: string | null
  title: string
  detail: string
  raisedBy: string | null
  seconds: number | null
  url: string | null
  category: string
  followUp: boolean
  link: { kind: RequestLinkKind; id: string; ref: string; title: string; state: string; url: string | null; tracked: boolean; by: 'ai' | 'staff'; created: boolean } | null
  hint: { kind: RequestLinkKind; id: string; ref: string; title: string; url: string | null; tracked: boolean; confidence: number } | null
  flag: RequestFlag | null
  history: RequestHistoryEntry[]
  related: { date: string; title: string }[]
  dismissed: { byName: string | null; at: string } | null
  suggestedCategory: string | null
  suggestedRequester: { _id: string; name: string; email: string } | null
}

export interface LinkTarget {
  id: string
  ref: string
  title: string
  state: string
}

export interface LinkTargets {
  tickets: LinkTarget[]
  issues: LinkTarget[]
}

export type RequestsStatus = 'pending' | 'done' | 'failed' | null

export type NotLoggedEntry =
  | { kind: 'call'; meetingId: string; title: string; date: string; requests: CallRequest[] }
  | { kind: 'email'; emailId: string; title: string; date: string; requests: CallRequest[] }

export interface ClientMeeting {
  _id: string
  firefliesId: string
  title: string
  date: string
  durationMinutes: number
  organizerEmail: string | null
  attendeeEmails: string[]
  overview: string | null
  shortSummary: string | null
  bullets: string | null
  keywords: string[]
  actionItems: MeetingActionItem[]
  summaryStatus: string | null
  requestsStatus: RequestsStatus
  requests: CallRequest[]
  url: string
  placedByName: string | null
}

export interface MeetingCandidate {
  companyId: string
  name: string
}

export interface WaitingMeeting {
  _id: string
  title: string
  date: string
  attendeeEmails: string[]
  candidates: MeetingCandidate[]
}

export interface ClientMeetings {
  meetings: ClientMeeting[]
  waiting: WaitingMeeting[]
  lastCheckedAt: string | null
  checked?: boolean
}

export interface RemovedMeeting {
  _id: string
  title: string
  date: string
  removedAt: string
  removedByName: string | null
}

export interface ClientDomain {
  domain: string
  source: 'users' | 'added'
}

export interface CompanyRow {
  companyId: string
  name: string
}

export interface LinkMismatches {
  notLinked: CompanyRow[]
  notOnHub: CompanyRow[]
}

export const SETTLED_SUMMARY = ['processed', 'skipped', 'failed']

/** The next call in the house date style, or null when none is set or it has passed. */
export function nextCallLabel(nextCallAt: string | null, now: Date = new Date()): string | null {
  if (!nextCallAt || new Date(nextCallAt).getTime() <= now.getTime()) return null
  return formatDateTime(nextCallAt)
}

/** "Today", "Tomorrow" or "In 7 days", counted in calendar days. */
export function daysUntilLabel(nextCallAt: string, now: Date = new Date()): string {
  const days = dayjs(nextCallAt).startOf('day').diff(dayjs(now).startOf('day'), 'day')
  if (days <= 0) return 'Today'
  return days === 1 ? 'Tomorrow' : `In ${days} days`
}

/** "01:26", or "1:02:03" past the first hour. */
export function callTimeLabel(seconds: number): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return hours ? `${hours}:${pad(minutes)}:${pad(seconds % 60)}` : `${pad(minutes)}:${pad(seconds % 60)}`
}
